import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import type {
  Property,
  SiteVisit,
  ChannelPartner,
  Project,
  WhatsAppMessage,
  KioskCheckInResult,
  CampaignAudienceLead,
  LeadSourceStat,
  AgentLeaderboardRow,
  AnalyticsMetrics,
  StalenessResult,
  SalesAgentOption,
  PropertyMedia,
  ChannelPartnerLeadStat,
  VisitOutcome,
  RecordVisitOutcomeOptions,
  RecordVisitOutcomeResult,
  SendDueVisitRemindersResult,
  TransitionUnitStatusOptions,
  TransitionUnitStatusResult,
  CreateBookingInput,
  CreateBookingResult,
  UnitHoldResult,
  UnitStatus,
  Booking,
  AgentCommissionRule,
  CpCommissionRow,
  PaymentSchedule,
  PortalSpendBySource,
  AgentScorecardRow,
  InventoryAgingRow,
} from "@/types/realEstate";
import type { Deal, ContactNote } from "../types";
import {
  isUuid,
  canTransitionUnitStatus,
  unitStatusToDealStage,
  buildConstructionLinkedSchedule,
  formatHoldUntilMarker,
  defaultHoldUntilIso,
  UNIT_STATUS_TRANSITIONS,
  DEFAULT_AGENT_COMMISSION_RULE,
  computeCommissions,
  requiresDiscountApproval,
  buildRejectedDiscountPatch,
  paymentSchedulesBlockDiscountReject,
  REJECT_DISCOUNT_PAYMENT_BLOCK_ERROR,
  extractMatchNote,
  unlinkLeadBlockedReason,
  canModifyUnitHold,
  HOLD_RESTRICTED_ERROR,
} from "../bookings/bookingUtils";
import {
  openWhatsAppWeb,
  sendMetaCloudWhatsApp,
  cleanIndianPhone,
} from "@/lib/whatsapp";
import { getSupabaseClient } from "./supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Empty DB must show empty UI — never invent inventory or leads.
 * FakeRest demo mode still fills lists via dataGenerator/realEstate.ts.
 */
export const EMPTY_ANALYTICS_METRICS: AnalyticsMetrics = {
  totalLeads: 0,
  siteVisitsConducted: 0,
  tokensBookingsLabel: "0 Units",
  grossBookingValueLabel: "₹0",
  usingSeed: false,
};

/** Apply getList result: success (including []) replaces previous; null keeps previous. */
export function applyFetchedList<T>(
  response: { data: T[] } | null | undefined,
  previous: T[],
): T[] {
  if (response == null) return previous;
  return response.data;
}

/** Legacy local-fallback ids — stripped once real UUID rows exist. */
const SEED_PROPERTY_IDS = new Set([
  "prop-1",
  "prop-2",
  "prop-3",
  "prop-4",
  "prop-5",
]);
const SEED_PROJECT_IDS = new Set(["p1", "p2", "p3"]);
const SEED_CP_IDS = new Set(["cp-1", "cp-2"]);
const SEED_VISIT_IDS = new Set(["sv-1"]);

const isSeedList = (ids: Set<string>, list: { id: string }[]) =>
  list.length > 0 && list.every((row) => ids.has(String(row.id)));

/** Replace whole seed pack with the first real row; otherwise prepend. */
const withRealRow = <T extends { id: string }>(
  prev: T[],
  seedIds: Set<string>,
  row: T,
): T[] => (isSeedList(seedIds, prev) ? [row] : [row, ...prev]);

const formatBudgetBracket = (
  min?: number | null,
  max?: number | null,
): string => {
  const toLabel = (n: number) => {
    if (n >= 10000000)
      return `${(n / 10000000).toFixed(1).replace(/\.0$/, "")}Cr`;
    if (n >= 100000) return `${(n / 100000).toFixed(0)}L`;
    return String(n);
  };
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${toLabel(min)}-${toLabel(max)}`;
  if (max != null && max >= 40000000) return "4.0Cr+";
  if (max != null) return `≤${toLabel(max)}`;
  return `${toLabel(min!)}+`;
};

const daysSince = (iso?: string | null): number => {
  if (!iso) return 999;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 999;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

const phoneFromJsonb = (phoneJsonb: unknown): string => {
  if (!Array.isArray(phoneJsonb) || phoneJsonb.length === 0) return "";
  const first = phoneJsonb[0] as { number?: string };
  return first?.number || "";
};

/** Contact (+ deal/booking meta) keyed by property id for Match/Hold/Booked units. */
export type LinkedContactByProperty = {
  id: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  deal_id?: string | number | null;
  agreement_value?: number | null;
  /** Offer / match note from deal.description (or Note: … suffix). */
  match_note?: string | null;
  /** True when an active (non-cancelled) booking links this unit — Unlink Lead is blocked. */
  has_active_booking?: boolean;
};

const buildLinkedContactByPropertyId = (
  bookings: Booking[],
  deals: Deal[],
  contactRows: Array<Record<string, unknown>>,
): Map<string, LinkedContactByProperty> => {
  const contactMap = new Map(contactRows.map((c) => [String(c.id), c]));
  const byProp = new Map<string, LinkedContactByProperty>();

  // Deals first (matched / on-hold units without a booking yet).
  for (const d of deals) {
    if (d.property_id == null || d.archived_at) continue;
    const contactId = d.contact_ids?.[0];
    if (contactId == null) continue;
    const propKey = String(d.property_id);
    if (byProp.has(propKey)) continue; // deals sorted DESC — keep newest
    const c = contactMap.get(String(contactId));
    byProp.set(propKey, {
      id: String(contactId),
      first_name: (c?.first_name as string | undefined) ?? undefined,
      last_name: (c?.last_name as string | undefined) ?? undefined,
      phone: c ? phoneFromJsonb(c.phone_jsonb) : undefined,
      deal_id: d.id ?? null,
      agreement_value: d.amount != null ? Number(d.amount) : null,
      match_note: extractMatchNote(d.description) ?? null,
      has_active_booking: false,
    });
  }

  // Bookings win for agreement_value / contact when present; keep deal note if any.
  for (const b of bookings) {
    if (b.property_id == null || b.contact_id == null) continue;
    const propKey = String(b.property_id);
    const existing = byProp.get(propKey);
    const c = contactMap.get(String(b.contact_id));
    byProp.set(propKey, {
      id: String(b.contact_id),
      first_name: (c?.first_name as string | undefined) ?? undefined,
      last_name: (c?.last_name as string | undefined) ?? undefined,
      phone: c ? phoneFromJsonb(c.phone_jsonb) : undefined,
      deal_id: b.deal_id ?? existing?.deal_id ?? null,
      agreement_value:
        b.agreement_value != null
          ? Number(b.agreement_value)
          : (existing?.agreement_value ?? null),
      match_note: existing?.match_note ?? null,
      has_active_booking: true,
    });
  }

  return byProp;
};

const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

const clampCommissionRate = (rate: number): boolean =>
  Number.isFinite(rate) && rate >= 0 && rate <= 100;

export const parseAgentCommissionRule = (raw: unknown): AgentCommissionRule => {
  try {
    if (!raw || typeof raw !== "object") return DEFAULT_AGENT_COMMISSION_RULE;
    const rule = raw as AgentCommissionRule;
    if (rule.type === "percent") {
      const rate = Number(rule.rate);
      if (clampCommissionRate(rate)) {
        return { type: "percent", rate };
      }
      return DEFAULT_AGENT_COMMISSION_RULE;
    }
    if (rule.type === "slab" && Array.isArray(rule.slabs)) {
      const slabs = rule.slabs
        .map((s) => ({
          minAgreementValue: Number(s.minAgreementValue),
          rate: Number(s.rate),
        }))
        .filter(
          (s) =>
            Number.isFinite(s.minAgreementValue) && clampCommissionRate(s.rate),
        );
      if (slabs.length > 0) return { type: "slab", slabs };
    }
    return DEFAULT_AGENT_COMMISSION_RULE;
  } catch {
    return DEFAULT_AGENT_COMMISSION_RULE;
  }
};

/** Legacy display-label keys → lead_source enum (portal spend migration). */
const LEGACY_PORTAL_SPEND_KEYS: Record<string, string> = {
  "Meta Ads": "Meta_Ads",
  "Google Ads": "Google_Ads",
};

export const migratePortalSpendKeys = (
  raw: PortalSpendBySource,
): PortalSpendBySource => {
  const result: PortalSpendBySource = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value == null) continue;
    const enumKey = LEGACY_PORTAL_SPEND_KEYS[key] || key;
    result[enumKey] = (result[enumKey] || 0) + value;
  }
  return result;
};

export const parsePortalSpend = (raw: unknown): PortalSpendBySource => {
  try {
    if (!raw || typeof raw !== "object") return {};
    const result: PortalSpendBySource = {};
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) result[key] = n;
    }
    return migratePortalSpendKeys(result);
  } catch {
    return {};
  }
};

/** Latest `[Unit Hold] {unit}` note date per unit number (for On_Hold aging). */
const buildHoldDateByUnit = (notes: ContactNote[]): Map<string, string> => {
  const holdDateByUnit = new Map<string, string>();
  for (const note of notes) {
    if (!note.text?.includes("[Unit Hold]")) continue;
    const match = note.text.match(/\[Unit Hold\]\s+([^\s·]+)/);
    if (!match) continue;
    const unit = match[1];
    const existing = holdDateByUnit.get(unit);
    if (
      !existing ||
      new Date(note.date).getTime() > new Date(existing).getTime()
    ) {
      holdDateByUnit.set(unit, note.date);
    }
  }
  return holdDateByUnit;
};

const buildInventoryAging = (
  propertiesList: Property[],
  holdDateByUnit: Map<string, string>,
): InventoryAgingRow[] =>
  propertiesList
    .filter(
      (p): p is Property & { status: "Available" | "On_Hold" } =>
        p.status === "Available" || p.status === "On_Hold",
    )
    .map((prop) => {
      // Available: approximate days since record creation (no status-change history yet).
      let anchorIso = prop.created_at;
      if (prop.status === "On_Hold") {
        anchorIso = holdDateByUnit.get(prop.unit_number) || prop.created_at;
      }
      return {
        propertyId: String(prop.id),
        unitNumber: prop.unit_number,
        projectName: prop.project?.name || "—",
        status: prop.status,
        daysInStatus: daysSince(
          prop.status === "Available" ? prop.created_at : anchorIso,
        ),
      };
    })
    .sort((a, b) => b.daysInStatus - a.daysInStatus);

export const rebuildInventoryAging = (
  propertiesList: Property[],
  notes: ContactNote[],
): InventoryAgingRow[] =>
  buildInventoryAging(propertiesList, buildHoldDateByUnit(notes));

export const buildCpCommissionLedger = (
  bookings: Booking[],
  contacts: Array<Record<string, unknown>>,
  propertiesList: Property[],
  partners: ChannelPartner[],
): CpCommissionRow[] => {
  const contactById = new Map(contacts.map((c) => [String(c.id), c]));
  const propertyById = new Map(propertiesList.map((p) => [String(p.id), p]));
  const cpById = new Map(partners.map((cp) => [String(cp.id), cp]));

  const rows: CpCommissionRow[] = [];
  for (const booking of bookings) {
    if (booking.cancelled_at) continue;
    const contact = contactById.get(String(booking.contact_id));
    if (!contact) continue;

    const cpId =
      booking.channel_partner_id != null
        ? String(booking.channel_partner_id)
        : contact.channel_partner_id
          ? String(contact.channel_partner_id)
          : "";
    if (!cpId) continue;

    const snapshotPct = Number(booking.cp_percentage);
    const cpPercentage =
      booking.cp_percentage != null && Number.isFinite(snapshotPct)
        ? snapshotPct
        : Number(cpById.get(cpId)?.commission_percentage) || 0;
    const agreementValue = Number(booking.agreement_value) || 0;
    const persisted = Number(booking.cp_commission_amount);
    const cpAmount =
      booking.cp_commission_amount != null &&
      Number.isFinite(persisted) &&
      persisted >= 0
        ? persisted
        : Math.round((agreementValue * cpPercentage) / 100);

    const buyerName =
      `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Lead";
    const prop = propertyById.get(String(booking.property_id));

    rows.push({
      bookingId: String(booking.id),
      channelPartnerId: cpId,
      bookingDate: booking.booking_date,
      buyerName,
      unitNumber: prop?.unit_number,
      agreementValue,
      cpPercentage,
      cpAmount,
      payoutStatus: booking.cp_payout_status || "pending",
    });
  }
  return rows;
};

type CurrentSaleRecord = {
  id?: string | number;
  administrator?: boolean;
};

const getCurrentSale = async (): Promise<CurrentSaleRecord | null> => {
  try {
    const storage = typeof window !== "undefined" ? window.localStorage : null;
    const cached = storage?.getItem(CURRENT_SALE_CACHE_KEY);
    if (cached) {
      const sale = JSON.parse(cached) as CurrentSaleRecord;
      if (sale?.id != null) return sale;
    }
    const { data: sessionData } = await getSupabaseClient().auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return null;
    const { data: sale } = await getSupabaseClient()
      .from("sales")
      .select("id, administrator")
      .match({ user_id: userId })
      .maybeSingle();
    return sale ?? null;
  } catch {
    return null;
  }
};

const getCurrentSalesId = async (): Promise<string | number | null> => {
  const sale = await getCurrentSale();
  return sale?.id ?? null;
};

const isCurrentUserManager = async (): Promise<boolean> => {
  const sale = await getCurrentSale();
  return Boolean(sale?.administrator);
};

const formatBookingMutationError = (e: unknown, fallback: string): string => {
  const raw = e instanceof Error ? e.message : String(e ?? fallback);
  if (raw.includes("Only managers can change booking money or payout fields")) {
    return "Only managers can change booking money or payout fields. Book at the existing agreement value, or ask a manager to update pricing.";
  }
  if (
    raw.includes(
      "Only managers can change booking discount approval or payout status",
    ) ||
    raw.toLowerCase().includes("permission denied") ||
    raw.toLowerCase().includes("row-level security")
  ) {
    return "Only managers can approve discounts or mark commissions as paid";
  }
  if (
    raw.includes("PL/pgSQL") ||
    raw.includes("RAISE") ||
    raw.includes("violates check constraint")
  ) {
    return fallback;
  }
  return raw.length > 200 ? fallback : raw;
};

const BOOKINGS_LIST_PAGE_SIZE = 1000;

export const REMINDER_24H_MARKER = "[reminder_24h_sent]";
export const REMINDER_2H_MARKER = "[reminder_2h_sent]";

export const hoursUntilScheduled = (
  scheduledAt: string,
  now = Date.now(),
): number => {
  const t = new Date(scheduledAt).getTime();
  if (Number.isNaN(t)) return NaN;
  return (t - now) / 3600000;
};

export const appendFeedbackLine = (
  existing: string | undefined,
  line: string,
): string => {
  const base = (existing || "").trim();
  return base ? `${base}\n${line}` : line;
};

export const outcomeToStatus = (outcome: VisitOutcome): SiteVisit["status"] => {
  switch (outcome) {
    case "Completed":
      return "Completed";
    case "No_Show":
      return "No_Show";
    case "Cancelled":
      return "Cancelled";
    case "Reschedule":
      return "Scheduled";
  }
};

export type VisitReminderKind = "24h" | "2h";

export const visitReminderKind = (
  visit: Pick<SiteVisit, "status" | "scheduled_at" | "feedback_notes">,
  now = Date.now(),
): VisitReminderKind | null => {
  if (visit.status !== "Scheduled") return null;
  const hours = hoursUntilScheduled(visit.scheduled_at, now);
  if (Number.isNaN(hours)) return null;
  const notes = visit.feedback_notes || "";
  if (hours >= 22 && hours <= 26 && !notes.includes(REMINDER_24H_MARKER)) {
    return "24h";
  }
  if (hours >= 1 && hours <= 3 && !notes.includes(REMINDER_2H_MARKER)) {
    return "2h";
  }
  return null;
};

export const tomorrowIsoDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString();
};

export const buildVisitReminderMessage = (
  visit: SiteVisit,
  prop: Property | undefined,
  kind: VisitReminderKind,
): string => {
  const unit = prop?.unit_number || "our project";
  const when = new Date(visit.scheduled_at).toLocaleString("en-IN");
  const maps =
    prop?.project?.google_maps_url ||
    prop?.project?.location_address ||
    "project site";
  if (kind === "24h") {
    return `Reminder: Your site visit for ${unit} is tomorrow at ${when}. Location: ${maps}. Reply if you need to reschedule.`;
  }
  return `Reminder: Your site visit for ${unit} starts in about 2 hours (${when}). Location: ${maps}. We look forward to seeing you!`;
};

export interface RealEstateContextType {
  projects: Project[];
  properties: Property[];
  channelPartners: ChannelPartner[];
  siteVisits: SiteVisit[];
  whatsappLogs: WhatsAppMessage[];
  /** Campaign audience from contacts (empty when no contacts). */
  campaignAudience: CampaignAudienceLead[];
  /** True only while using a non-DB demo pack — always false after Wave 0. */
  usingAudienceSeed: boolean;
  leadSourceStats: LeadSourceStat[];
  agentLeaderboard: AgentLeaderboardRow[];
  agentScorecard: AgentScorecardRow[];
  inventoryAging: InventoryAgingRow[];
  portalSpend: PortalSpendBySource;
  analyticsMetrics: AnalyticsMetrics;
  salesAgents: SalesAgentOption[];
  propertyMedia: PropertyMedia[];
  channelPartnerLeadStats: ChannelPartnerLeadStat[];
  addProperty: (prop: Partial<Property>) => void;
  updateProperty: (
    id: string,
    patch: Partial<Property>,
  ) => Promise<Property | null>;
  transitionUnitStatus: (
    propertyId: string,
    nextStatus: UnitStatus,
    options?: TransitionUnitStatusOptions,
  ) => Promise<TransitionUnitStatusResult>;
  createBooking: (input: CreateBookingInput) => Promise<CreateBookingResult>;
  agentCommissionRule: AgentCommissionRule;
  cpCommissionLedger: CpCommissionRow[];
  refreshCommissionLedger: () => Promise<void>;
  approveBookingDiscount: (
    bookingId: string,
    approve: boolean,
  ) => Promise<{ ok: boolean; error?: string }>;
  markCommissionPaid: (
    bookingId: string,
    party: "cp" | "agent",
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Cancel booking: refund ledger + release unit to Available. */
  cancelBooking: (
    bookingId: string,
    input: { deductionPercent: number; reason?: string },
  ) => Promise<{
    ok: boolean;
    error?: string;
    refundAmount?: number;
    deductionAmount?: number;
  }>;
  holdUnitForLead: (
    propertyId: string,
    contactId: string | number,
    holdUntilIso?: string,
  ) => Promise<UnitHoldResult>;
  releaseUnitHold: (propertyId: string) => Promise<UnitHoldResult>;
  /**
   * Clear deal↔property match/hold link. Blocks when an active booking exists
   * (use cancelBooking). On_Hold units are released to Available.
   */
  unlinkLeadFromProperty: (propertyId: string) => Promise<UnitHoldResult>;
  deleteProperty: (id: string) => Promise<boolean>;
  upsertPropertyBrochure: (
    propertyId: string,
    mediaUrl: string,
  ) => Promise<PropertyMedia | null>;
  addProject: (project: Partial<Project>) => Promise<Project | null>;
  addSiteVisit: (
    visit: Partial<SiteVisit>,
    buyerPhone: string,
    sendWhatsApp: boolean,
  ) => Promise<{ ok: boolean; persisted: boolean; error?: string }>;
  recordVisitOutcome: (
    visitId: string,
    outcome: VisitOutcome,
    options?: RecordVisitOutcomeOptions,
  ) => Promise<RecordVisitOutcomeResult>;
  sendDueVisitReminders: () => Promise<SendDueVisitRemindersResult>;
  addChannelPartner: (cp: Partial<ChannelPartner>) => void;
  updateChannelPartner: (
    id: string,
    patch: Partial<ChannelPartner>,
  ) => Promise<ChannelPartner | null>;
  deleteChannelPartner: (id: string) => Promise<boolean>;
  addVisitorFromKiosk: (
    name: string,
    phone: string,
    visitType: "direct" | "cp",
    cpId?: string,
    projectId?: string,
  ) => Promise<KioskCheckInResult>;
  /** Loaded on demand (RLS-scoped); not part of bulk fetchAllData. */
  refreshCampaignAudience: () => Promise<void>;
  triggerWhatsAppMessage: (
    phone: string,
    text: string,
    openWeb?: boolean,
    contactId?: string | number | null,
    source?: "campaign" | "agent" | "auto_first_touch" | string,
    mediaUrl?: string | null,
    extras?: { templateName?: string; templateLanguage?: string },
  ) => Promise<void>;
  runStalenessReassignment: () => Promise<StalenessResult>;
  /**
   * Contact (+ deal/booking meta) keyed by property id — active deals and bookings.
   * Used for Match/Hold/Booked linked-contact UI and Change Lead.
   */
  bookedContactByPropertyId: Map<string, LinkedContactByProperty>;
  /** Re-load linked contacts from deals + bookings (call after match/hold/booking). */
  refreshBookedContacts: () => Promise<void>;
}

const RealEstateContext = createContext<RealEstateContextType | undefined>(
  undefined,
);

const useSafeDataProvider = () => {
  try {
    return useDataProvider();
  } catch {
    return null;
  }
};

export const RealEstateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dataProvider = useSafeDataProvider();
  const { restrictHoldChangesToHolder } = useConfigurationContext();

  const [projects, setProjects] = useState<Project[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [channelPartners, setChannelPartners] = useState<ChannelPartner[]>([]);
  const [siteVisits, setSiteVisits] = useState<SiteVisit[]>([]);
  const [whatsappLogs, setWhatsappLogs] = useState<WhatsAppMessage[]>([]);
  const [campaignAudience, setCampaignAudience] = useState<
    CampaignAudienceLead[]
  >([]);
  const [usingAudienceSeed, setUsingAudienceSeed] = useState(false);
  const [leadSourceStats, setLeadSourceStats] = useState<LeadSourceStat[]>([]);
  const [agentLeaderboard, setAgentLeaderboard] = useState<
    AgentLeaderboardRow[]
  >([]);
  const [agentScorecard, setAgentScorecard] = useState<AgentScorecardRow[]>([]);
  const [inventoryAging, setInventoryAging] = useState<InventoryAgingRow[]>([]);
  const [portalSpend, setPortalSpend] = useState<PortalSpendBySource>({});
  const [analyticsMetrics, setAnalyticsMetrics] = useState<AnalyticsMetrics>(
    EMPTY_ANALYTICS_METRICS,
  );
  const [salesAgents, setSalesAgents] = useState<SalesAgentOption[]>([]);
  const [propertyMedia, setPropertyMedia] = useState<PropertyMedia[]>([]);
  const [channelPartnerLeadStats, setChannelPartnerLeadStats] = useState<
    ChannelPartnerLeadStat[]
  >([]);
  const [agentCommissionRule, setAgentCommissionRule] =
    useState<AgentCommissionRule>(DEFAULT_AGENT_COMMISSION_RULE);
  const [cpCommissionLedger, setCpCommissionLedger] = useState<
    CpCommissionRow[]
  >([]);
  const [contactNotes, setContactNotes] = useState<ContactNote[]>([]);
  const [bookedContactByPropertyId, setBookedContactByPropertyId] = useState<
    Map<string, LinkedContactByProperty>
  >(new Map());

  const refreshBookedContacts = useCallback(async () => {
    if (!dataProvider) return;
    try {
      const [bookingRes, dealsRes] = await Promise.all([
        dataProvider
          .getList("bookings", {
            pagination: { page: 1, perPage: BOOKINGS_LIST_PAGE_SIZE },
            sort: { field: "created_at", order: "DESC" },
            filter: { "cancelled_at@is": null },
          })
          .catch(() => null),
        dataProvider
          .getList<Deal>("deals", {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "updated_at", order: "DESC" },
            filter: { "archived_at@is": null },
          })
          .catch(() => null),
      ]);

      const bookings = (bookingRes?.data || []) as Booking[];
      const deals = ((dealsRes?.data || []) as Deal[]).filter(
        (d) => d.property_id != null,
      );

      const contactIds = [
        ...new Set([
          ...bookings
            .map((b) => b.contact_id)
            .filter((id): id is number | string => id != null)
            .map(String),
          ...deals
            .map((d) => d.contact_ids?.[0])
            .filter((id): id is number | string => id != null)
            .map(String),
        ]),
      ];

      let contactRows: Array<Record<string, unknown>> = [];
      if (contactIds.length > 0) {
        const contactRes = await dataProvider
          .getList("contacts", {
            pagination: { page: 1, perPage: Math.max(contactIds.length, 50) },
            sort: { field: "id", order: "ASC" },
            filter: { "id@in": `(${contactIds.join(",")})` },
          })
          .catch(() => null);
        contactRows = (contactRes?.data || []) as Array<
          Record<string, unknown>
        >;
      }

      setBookedContactByPropertyId(
        buildLinkedContactByPropertyId(bookings, deals, contactRows),
      );
    } catch {
      // Leave current map on failure
    }
  }, [dataProvider]);

  // Fetch from dataProvider. Empty lists stay empty (no invented seed).
  // FakeRest demo mode still returns rows from dataGenerator/realEstate.ts.
  const refreshCommissionLedger = useCallback(async () => {
    if (!dataProvider) return;
    try {
      const [bookingRes, contactRes] = await Promise.all([
        dataProvider
          .getList("bookings", {
            pagination: { page: 1, perPage: BOOKINGS_LIST_PAGE_SIZE },
            sort: { field: "created_at", order: "DESC" },
            filter: { "cancelled_at@is": null },
          })
          .catch(() => null),
        dataProvider
          .getList("contacts", {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "id", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
      ]);
      const bookings = (bookingRes?.data || []) as Booking[];
      const contacts = (contactRes?.data || []) as Array<
        Record<string, unknown>
      >;
      setCpCommissionLedger(
        buildCpCommissionLedger(
          bookings,
          contacts,
          properties,
          channelPartners,
        ),
      );
    } catch {
      // Keep previous ledger on failure
    }
  }, [dataProvider, properties, channelPartners]);

  const fetchAllData = useCallback(async () => {
    if (!dataProvider) return;
    let loadedPortalSpend: PortalSpendBySource = {};
    try {
      try {
        const dp = dataProvider as {
          getConfiguration?: () => Promise<Record<string, unknown>>;
        };
        if (typeof dp.getConfiguration === "function") {
          const config = await dp.getConfiguration();
          setAgentCommissionRule(
            parseAgentCommissionRule(config.agentCommission),
          );
          loadedPortalSpend = parsePortalSpend(config.portalSpend);
          setPortalSpend(loadedPortalSpend);
        } else {
          const cfgRes = await dataProvider
            .getOne("configuration", { id: 1 })
            .catch(() => null);
          const config = (
            cfgRes?.data as { config?: Record<string, unknown> } | undefined
          )?.config;
          setAgentCommissionRule(
            parseAgentCommissionRule(config?.agentCommission),
          );
          loadedPortalSpend = parsePortalSpend(config?.portalSpend);
          setPortalSpend(loadedPortalSpend);
        }
      } catch {
        setAgentCommissionRule(DEFAULT_AGENT_COMMISSION_RULE);
        setPortalSpend({});
      }

      const [
        projRes,
        propRes,
        cpRes,
        visitRes,
        salesRes,
        bookingRes,
        mediaRes,
        dealsRes,
      ] = await Promise.all([
        dataProvider
          .getList("projects", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList("properties", {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList("channel_partners", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList("site_visits", {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList("sales", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList("bookings", {
            pagination: { page: 1, perPage: BOOKINGS_LIST_PAGE_SIZE },
            sort: { field: "created_at", order: "DESC" },
            filter: { "cancelled_at@is": null },
          })
          .catch(() => null),
        dataProvider
          .getList("property_media", {
            pagination: { page: 1, perPage: 200 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => null),
        dataProvider
          .getList<Deal>("deals", {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "updated_at", order: "DESC" },
            filter: { "archived_at@is": null },
          })
          .catch(() => null),
      ]);

      const dbProjects = applyFetchedList(
        projRes as { data: Project[] } | null,
        [],
      );
      const dbProperties = applyFetchedList(
        propRes as { data: Property[] } | null,
        [],
      );

      const withProjects = (props: Property[], projs: Project[]) => {
        const byId = new Map(projs.map((p) => [String(p.id), p]));
        return props.map((prop) => ({
          ...prop,
          project: byId.get(String(prop.project_id)) || prop.project,
        }));
      };

      // Always apply successful fetches — including empty arrays.
      if (projRes) setProjects(dbProjects);
      if (propRes) {
        setProperties(withProjects(dbProperties, dbProjects));
      }

      setPropertyMedia(
        mediaRes?.data ? (mediaRes.data as PropertyMedia[]) : [],
      );

      if (cpRes) {
        setChannelPartners(cpRes.data as ChannelPartner[]);
      }

      if (visitRes) {
        setSiteVisits(visitRes.data as SiteVisit[]);
      }

      const salesRows = (salesRes?.data || []) as Array<{
        id: string | number;
        first_name?: string;
        last_name?: string;
      }>;
      if (salesRes) {
        setSalesAgents(
          salesRows.map((s) => ({
            id: s.id,
            name:
              `${s.first_name || ""} ${s.last_name || ""}`.trim() || "Agent",
          })),
        );
      }

      setUsingAudienceSeed(false);
      // Do not clear campaignAudience here — PropertyCatalog / campaigns load it via
      // refreshCampaignAudience; wiping races that refresh and empties Match Lead.
      setChannelPartnerLeadStats([]);
      setLeadSourceStats([]);
      setAgentLeaderboard([]);
      setAgentScorecard([]);
      setInventoryAging([]);
      setAnalyticsMetrics(EMPTY_ANALYTICS_METRICS);

      const bookings = (bookingRes?.data || []) as Booking[];
      const deals = ((dealsRes?.data || []) as Deal[]).filter(
        (d) => d.property_id != null,
      );
      const contactIds = [
        ...new Set([
          ...bookings
            .map((b) => b.contact_id)
            .filter((id): id is number | string => id != null)
            .map(String),
          ...deals
            .map((d) => d.contact_ids?.[0])
            .filter((id): id is number | string => id != null)
            .map(String),
        ]),
      ];
      let contactRows: Array<Record<string, unknown>> = [];
      if (contactIds.length > 0) {
        const contactRes = await dataProvider
          .getList("contacts", {
            pagination: { page: 1, perPage: Math.max(contactIds.length, 50) },
            sort: { field: "id", order: "ASC" },
            filter: { "id@in": `(${contactIds.join(",")})` },
          })
          .catch(() => null);
        contactRows = (contactRes?.data || []) as Array<
          Record<string, unknown>
        >;
      }

      if (bookingRes?.data) {
        setCpCommissionLedger(
          buildCpCommissionLedger(
            bookings,
            contactRows,
            withProjects(dbProperties, dbProjects),
            cpRes?.data ? (cpRes.data as ChannelPartner[]) : [],
          ),
        );
      } else {
        setCpCommissionLedger([]);
      }

      setBookedContactByPropertyId(
        buildLinkedContactByPropertyId(bookings, deals, contactRows),
      );
    } catch {
      // Leave current state on total failure (starts empty — no fake seed).
    }
  }, [dataProvider]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const addProperty = async (newProp: Partial<Property>) => {
    const proj =
      projects.find((p) => p.id === newProp.project_id) || projects[0];
    const fullProp: Partial<Property> = {
      project_id: newProp.project_id || proj?.id,
      unit_number: newProp.unit_number || "New Unit",
      property_type: newProp.property_type || "Flat",
      configuration: newProp.configuration || "3BHK",
      floor_number: Number(newProp.floor_number) || 1,
      carpet_area_sqft: Number(newProp.carpet_area_sqft) || 1200,
      base_price: Number(newProp.base_price) || 10000000,
      facing: newProp.facing || "East",
      status: "Available",
      amenities: ["Park View", "2 Car Parking"],
      created_at: new Date().toISOString(),
    };

    try {
      if (dataProvider) {
        const created = await dataProvider.create("properties", {
          data: fullProp,
        });
        if (created?.data) {
          const row = { ...created.data, project: proj } as Property;
          setProperties((prev) => withRealRow(prev, SEED_PROPERTY_IDS, row));
          return;
        }
      }
    } catch {
      // Local fallback
    }

    const fallbackProp: Property = {
      ...(fullProp as Property),
      id: "prop-" + Date.now(),
      project: proj,
    };
    setProperties((prev) => withRealRow(prev, SEED_PROPERTY_IDS, fallbackProp));
  };

  const updateProperty = async (
    id: string,
    patch: Partial<Property>,
  ): Promise<Property | null> => {
    const existing = properties.find((p) => String(p.id) === String(id));
    if (!existing) return null;

    if (
      patch.status != null &&
      patch.status !== existing.status &&
      !canTransitionUnitStatus(existing.status, patch.status)
    ) {
      return null;
    }

    const next = { ...existing, ...patch } as Property;
    const isSeed = SEED_PROPERTY_IDS.has(String(id));

    try {
      if (dataProvider && !isSeed) {
        const { project: _ignored, ...data } = patch as Partial<Property> & {
          project?: Property["project"];
        };
        void _ignored;
        const updated = await dataProvider.update("properties", {
          id,
          data,
          previousData: existing,
        });
        if (updated?.data) {
          const row = {
            ...updated.data,
            project: existing.project,
          } as Property;
          setProperties((prev) =>
            prev.map((p) => (String(p.id) === String(id) ? row : p)),
          );
          return row;
        }
      }
    } catch {
      // fall through to local
    }

    setProperties((prev) =>
      prev.map((p) => (String(p.id) === String(id) ? next : p)),
    );
    return next;
  };

  const isNumericContactId = (
    id: string | number | undefined,
  ): id is string | number => id != null && /^\d+$/.test(String(id));

  const syncDealForUnit = async (
    contactId: string | number | undefined,
    propertyId: string,
    nextStatus: UnitStatus,
    dealId?: string | number,
  ): Promise<void> => {
    if (!dataProvider) return;

    const stage = unitStatusToDealStage(nextStatus);
    if (!stage) return;

    try {
      let deals: Deal[] = [];

      if (dealId != null) {
        try {
          const one = await dataProvider.getOne<Deal>("deals", { id: dealId });
          if (one?.data) deals = [one.data];
        } catch {
          // fall through to list lookup
        }
      }

      if (!deals.length && isNumericContactId(contactId)) {
        const contactNum = Number(contactId);
        const listed = await dataProvider.getList<Deal>("deals", {
          filter: { "contact_ids@cs": `{${contactNum}}` },
          pagination: { page: 1, perPage: 100 },
          sort: { field: "id", order: "ASC" },
        });
        deals = listed.data || [];
      }

      if (!deals.length) {
        // Manual status change: find deal already linked to this unit
        const listed = await dataProvider.getList<Deal>("deals", {
          filter: { "property_id@eq": propertyId },
          pagination: { page: 1, perPage: 20 },
          sort: { field: "id", order: "DESC" },
        });
        deals = listed.data || [];
      }

      if (!deals.length) return;

      let target =
        dealId != null
          ? deals.find((d) => String(d.id) === String(dealId))
          : undefined;
      if (!target) {
        target = deals.find(
          (d) =>
            d.property_id != null &&
            String(d.property_id) === String(propertyId),
        );
      }
      // Never fall back to an unrelated deal for multi-deal contacts
      if (!target) return;

      const patch: Partial<Deal> = { stage };
      if (!target.property_id) {
        patch.property_id = propertyId;
      }

      await dataProvider.update("deals", {
        id: target.id,
        data: patch,
        previousData: target,
      });
    } catch {
      // Non-blocking: unit status already saved
    }
  };

  const transitionUnitStatus = async (
    propertyId: string,
    nextStatus: UnitStatus,
    options?: TransitionUnitStatusOptions,
  ): Promise<TransitionUnitStatusResult> => {
    if (!isUuid(propertyId) || SEED_PROPERTY_IDS.has(String(propertyId))) {
      return {
        ok: false,
        error:
          "Property must be a persisted UUID (seed units cannot transition)",
      };
    }

    const existing = properties.find(
      (p) => String(p.id) === String(propertyId),
    );
    if (!existing) {
      return { ok: false, error: "Property not found" };
    }

    const currentStatus = existing.status;
    if (
      !canTransitionUnitStatus(currentStatus, nextStatus, {
        allowBookingCancel: options?.allowBookingCancel,
      })
    ) {
      const allowed = UNIT_STATUS_TRANSITIONS[currentStatus] || [];
      return {
        ok: false,
        error: `Cannot transition from ${currentStatus} to ${nextStatus}. Allowed: ${allowed.join(", ") || "none"}`,
      };
    }

    const leavingHold = currentStatus === "On_Hold" && nextStatus !== "On_Hold";
    if (leavingHold) {
      const sale = await getCurrentSale();
      if (
        !canModifyUnitHold({
          restrictHoldChangesToHolder,
          heldBy: existing.held_by,
          currentSalesId: sale?.id != null ? String(sale.id) : null,
          isManager: Boolean(sale?.administrator),
        })
      ) {
        return { ok: false, error: HOLD_RESTRICTED_ERROR };
      }
    }

    const holdUntil =
      nextStatus === "On_Hold"
        ? options?.holdUntilIso || defaultHoldUntilIso()
        : null;

    let heldByPatch: { held_by: string | number | null } | undefined;
    if (nextStatus === "On_Hold") {
      const salesId = await getCurrentSalesId();
      if (salesId == null) {
        return {
          ok: false,
          error:
            "Could not identify the holding agent. Sign in again and retry.",
        };
      }
      heldByPatch = { held_by: salesId };
    } else if (leavingHold || nextStatus === "Available") {
      heldByPatch = { held_by: null };
    }

    const updated = await updateProperty(propertyId, {
      status: nextStatus,
      ...(nextStatus === "On_Hold"
        ? { hold_until: holdUntil }
        : { hold_until: null }),
      ...heldByPatch,
    });
    if (!updated) {
      return { ok: false, error: "Failed to update property status" };
    }
    // Ensure held_by from our patch is kept even if the API response omits it.
    if (
      heldByPatch &&
      String(updated.held_by ?? "") !== String(heldByPatch.held_by ?? "")
    ) {
      updated.held_by = heldByPatch.held_by;
      setProperties((prev) =>
        prev.map((p) =>
          String(p.id) === String(propertyId)
            ? { ...p, held_by: heldByPatch.held_by }
            : p,
        ),
      );
    }

    let notesForAging = contactNotes;
    if (
      nextStatus === "On_Hold" &&
      isNumericContactId(options?.contactId) &&
      dataProvider
    ) {
      try {
        const holdUntil = options?.holdUntilIso || defaultHoldUntilIso();
        const noteText = [
          `[Unit Hold] ${existing.unit_number}`,
          existing.configuration,
          formatHoldUntilMarker(holdUntil),
        ]
          .filter(Boolean)
          .join(" · ");
        const noteDate = new Date().toISOString();
        const created = await dataProvider.create("contact_notes", {
          data: {
            contact_id: Number(options.contactId),
            text: noteText,
            date: noteDate,
            status: "warm",
          },
        });
        const newNote = (created?.data ?? {
          id: `local-${Date.now()}`,
          contact_id: Number(options.contactId),
          text: noteText,
          date: noteDate,
          status: "warm",
        }) as ContactNote;
        notesForAging = [...contactNotes, newNote];
        setContactNotes(notesForAging);
      } catch {
        // Non-blocking: hold status already saved
      }
    }

    if (!options?.skipDealSync) {
      await syncDealForUnit(
        options?.contactId,
        propertyId,
        nextStatus,
        options?.dealId,
      );
    }

    const affectsAging =
      nextStatus === "Available" ||
      nextStatus === "On_Hold" ||
      currentStatus === "Available" ||
      currentStatus === "On_Hold";
    if (affectsAging) {
      setProperties((prev) => {
        setInventoryAging(rebuildInventoryAging(prev, notesForAging));
        return prev;
      });
    }

    return { ok: true, property: updated };
  };

  const createBooking = async (
    input: CreateBookingInput,
  ): Promise<CreateBookingResult> => {
    const { contactId, propertyId, dealId, buyerName: _buyerName } = input;
    void _buyerName;

    if (!isUuid(propertyId) || SEED_PROPERTY_IDS.has(String(propertyId))) {
      return {
        ok: false,
        error:
          "Property must be a persisted UUID (seed units cannot be booked)",
      };
    }
    if (!isNumericContactId(contactId)) {
      return { ok: false, error: "Contact id must be numeric" };
    }

    const property = properties.find(
      (p) => String(p.id) === String(propertyId),
    );
    if (!property) {
      return { ok: false, error: "Property not found" };
    }

    const listPrice =
      input.listPrice != null
        ? Number(input.listPrice)
        : Number(property.base_price) || 0;
    const agreementValue = input.agreementValue ?? listPrice;
    if (agreementValue <= 0) {
      return { ok: false, error: "Agreement value must be greater than zero" };
    }
    const discountAmount =
      input.discountAmount != null
        ? Math.max(0, Number(input.discountAmount))
        : Math.max(0, listPrice - agreementValue);

    if (!canTransitionUnitStatus(property.status, "Booked")) {
      const allowed = UNIT_STATUS_TRANSITIONS[property.status] || [];
      return {
        ok: false,
        error: `Cannot book unit from status ${property.status}. Allowed next: ${allowed.join(", ") || "none"}`,
      };
    }

    if (!dataProvider) {
      return { ok: false, error: "Data provider unavailable" };
    }

    let channelPartnerId: string | null = null;
    let cpPercentage: number | null = null;
    try {
      const contactRes = await dataProvider.getOne("contacts", {
        id: Number(contactId),
      });
      const cpId = (
        contactRes?.data as { channel_partner_id?: string | number } | undefined
      )?.channel_partner_id;
      if (cpId != null) {
        channelPartnerId = String(cpId);
        const cp = channelPartners.find((c) => String(c.id) === String(cpId));
        if (cp?.commission_percentage != null) {
          cpPercentage = Number(cp.commission_percentage);
        }
      }
    } catch {
      channelPartnerId = null;
      cpPercentage = null;
    }

    const commissions = computeCommissions({
      agreementValue,
      cpPercentage: cpPercentage ?? 0,
      agentRule: agentCommissionRule,
    });
    const discountStatus = requiresDiscountApproval(listPrice, agreementValue)
      ? "pending"
      : "none";

    const bookingAmount = Math.round(agreementValue * 0.1);
    let bookingId: string | undefined;
    let isNew = false;

    // Check for a pre-existing booking on this property (UNIQUE property_id constraint).
    let existingBooking: Booking | undefined;
    try {
      const existing = await dataProvider.getList<Booking>("bookings", {
        filter: { "property_id@eq": propertyId },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "DESC" },
      });
      existingBooking = existing?.data?.[0];
    } catch {
      return {
        ok: false,
        error: "Could not check for existing booking — please try again",
      };
    }

    // Non-money fields agents may always update. Money/payout columns are
    // guarded by bookings_guard_money_fields (managers only on UPDATE).
    const nonMoneyPayload = {
      contact_id: Number(contactId),
      property_id: propertyId,
      ...(dealId != null ? { deal_id: Number(dealId) || dealId } : {}),
      booking_amount: bookingAmount,
      list_price: listPrice,
      channel_partner_id: channelPartnerId,
      cp_percentage: cpPercentage,
      payment_plan: "Construction_Linked" as const,
      booking_date: new Date().toISOString().slice(0, 10),
      documents_verified: false,
    };
    const moneyPayload = {
      agreement_value: agreementValue,
      discount_amount: discountAmount,
      discount_status: discountStatus,
      cp_commission_amount: commissions.cpAmount,
      agent_commission_amount: commissions.agentAmount,
    };

    try {
      if (existingBooking?.id) {
        bookingId = String(existingBooking.id);
        // isNew stays false — do not delete on rollback.

        const prevAgreement = Number(existingBooking.agreement_value);
        const prevDiscount = Number(existingBooking.discount_amount ?? 0);
        const prevDiscountStatus = existingBooking.discount_status ?? "none";
        const moneyChanged =
          prevAgreement !== agreementValue ||
          prevDiscount !== discountAmount ||
          prevDiscountStatus !== discountStatus;

        const manager = await isCurrentUserManager();
        if (moneyChanged && !manager) {
          return {
            ok: false,
            error:
              "Only managers can change booking money or payout fields. Book at the existing agreement value, or ask a manager to update pricing.",
          };
        }

        if (moneyChanged) {
          // Reconcile schedules only when agreement value is changing (manager).
          const existingSchedulesRes =
            await dataProvider.getList<PaymentSchedule>("payment_schedules", {
              filter: { "booking_id@eq": bookingId },
              pagination: { page: 1, perPage: 100 },
              sort: { field: "id", order: "ASC" },
            });
          const existingSchedules: PaymentSchedule[] =
            existingSchedulesRes?.data ?? [];
          const newSchedule = buildConstructionLinkedSchedule(
            bookingId,
            agreementValue,
          );
          const matchedExistingIds = new Set<string>();
          await Promise.all(
            newSchedule.map(async (row) => {
              const match = existingSchedules.find(
                (s) => s.milestone_name === row.milestone_name,
              );
              if (match) {
                matchedExistingIds.add(match.id);
                await dataProvider.update("payment_schedules", {
                  id: match.id,
                  data: {
                    amount_due: row.amount_due,
                    due_date: row.due_date,
                    status: "PENDING",
                    amount_paid: 0,
                    due_percentage: row.due_percentage,
                  },
                  previousData: match,
                });
              } else {
                await dataProvider.create("payment_schedules", { data: row });
              }
            }),
          );
          await Promise.all(
            existingSchedules
              .filter((s) => !matchedExistingIds.has(s.id))
              .map(async (row) => {
                try {
                  await dataProvider.delete("payment_schedules", {
                    id: row.id,
                    previousData: row,
                  });
                } catch {
                  // permission denied for non-admin — ignore
                }
              }),
          );
        }

        await dataProvider.update<Booking>("bookings", {
          id: bookingId,
          data: {
            ...nonMoneyPayload,
            ...(moneyChanged ? moneyPayload : {}),
          },
          previousData: existingBooking,
        });
      } else {
        const created = await dataProvider.create<Booking>("bookings", {
          data: {
            ...nonMoneyPayload,
            ...moneyPayload,
            cp_payout_status: "pending",
            agent_payout_status: "pending",
          },
        });
        if (!created?.data?.id) {
          return { ok: false, error: "Failed to create booking" };
        }
        bookingId = String(created.data.id);
        isNew = true;

        const schedule = buildConstructionLinkedSchedule(
          bookingId,
          agreementValue,
        );
        await Promise.all(
          schedule.map((row) =>
            dataProvider.create("payment_schedules", { data: row }),
          ),
        );
      }
    } catch (e) {
      if (isNew && bookingId && dataProvider) {
        try {
          await dataProvider.delete("bookings", {
            id: bookingId,
            previousData: { id: bookingId } as Booking,
          });
        } catch {
          // best-effort rollback
        }
      }
      return {
        ok: false,
        error: formatBookingMutationError(e, "Failed to create booking"),
      };
    }

    const transition = await transitionUnitStatus(propertyId, "Booked", {
      contactId,
      dealId,
      skipDealSync: false,
    });
    if (!transition.ok) {
      if (isNew && bookingId && dataProvider) {
        try {
          await dataProvider.delete("bookings", {
            id: bookingId,
            previousData: { id: bookingId } as Booking,
          });
        } catch {
          // best-effort rollback
        }
      }
      return {
        ok: false,
        error: isNew
          ? transition.error ||
            "Could not mark unit Booked — booking rolled back"
          : transition.error || "Could not mark unit Booked",
      };
    }

    try {
      await refreshCommissionLedger();
    } catch {
      // Non-blocking: booking already succeeded
    }
    void refreshBookedContacts();

    return { ok: true, bookingId };
  };

  const approveBookingDiscount = async (
    bookingId: string,
    approve: boolean,
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!dataProvider) {
      return { ok: false, error: "Data provider unavailable" };
    }
    if (!(await isCurrentUserManager())) {
      return {
        ok: false,
        error: "Only managers can approve or reject booking discounts",
      };
    }
    try {
      const existing = await dataProvider.getOne<Booking>("bookings", {
        id: bookingId,
      });
      if (!existing?.data) {
        return { ok: false, error: "Booking not found" };
      }
      if (existing.data.discount_status !== "pending") {
        return { ok: false, error: "Discount is not pending approval" };
      }
      const salesId = await getCurrentSalesId();

      if (approve) {
        await dataProvider.update("bookings", {
          id: bookingId,
          data: {
            discount_status: "approved",
            discount_approved_by: salesId,
          },
          previousData: existing.data,
        });
      } else {
        const schedRes = await dataProvider.getList<PaymentSchedule>(
          "payment_schedules",
          {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "due_percentage", order: "ASC" },
            filter: { "booking_id@eq": bookingId },
          },
        );
        const schedules = schedRes.data || [];
        if (paymentSchedulesBlockDiscountReject(schedules)) {
          return { ok: false, error: REJECT_DISCOUNT_PAYMENT_BLOCK_ERROR };
        }

        const { patch, restoredAgreementValue } = buildRejectedDiscountPatch({
          booking: existing.data,
          agentRule: agentCommissionRule,
          salesId,
        });

        await dataProvider.update("bookings", {
          id: bookingId,
          data: patch,
          previousData: existing.data,
        });

        await Promise.all(
          schedules.map((row) =>
            dataProvider.delete("payment_schedules", {
              id: row.id,
              previousData: row,
            }),
          ),
        );

        const rebuilt = buildConstructionLinkedSchedule(
          bookingId,
          restoredAgreementValue,
        );
        await Promise.all(
          rebuilt.map((row) =>
            dataProvider.create("payment_schedules", { data: row }),
          ),
        );
      }

      await refreshCommissionLedger();
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatBookingMutationError(
          e,
          "Failed to update discount status",
        ),
      };
    }
  };

  const markCommissionPaid = async (
    bookingId: string,
    party: "cp" | "agent",
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!dataProvider) {
      return { ok: false, error: "Data provider unavailable" };
    }
    if (!(await isCurrentUserManager())) {
      return {
        ok: false,
        error: "Only managers can mark commissions as paid",
      };
    }
    try {
      const existing = await dataProvider.getOne<Booking>("bookings", {
        id: bookingId,
      });
      if (!existing?.data) {
        return { ok: false, error: "Booking not found" };
      }
      const field = party === "cp" ? "cp_payout_status" : "agent_payout_status";
      await dataProvider.update("bookings", {
        id: bookingId,
        data: { [field]: "paid" },
        previousData: existing.data,
      });
      await refreshCommissionLedger();
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatBookingMutationError(e, "Failed to mark commission paid"),
      };
    }
  };

  const cancelBooking = async (
    bookingId: string,
    input: { deductionPercent: number; reason?: string },
  ): Promise<{
    ok: boolean;
    error?: string;
    refundAmount?: number;
    deductionAmount?: number;
  }> => {
    if (!(await isCurrentUserManager())) {
      return {
        ok: false,
        error: "Only managers can cancel bookings",
      };
    }
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc("cancel_booking", {
        p_booking_id: bookingId,
        p_deduction_percent: input.deductionPercent,
        p_reason: input.reason?.trim() || null,
      });
      if (error) throw error;
      if (!data?.ok) {
        const errCode = String(data?.error || "");
        const friendly =
          errCode === "booking_not_found"
            ? "Booking not found"
            : errCode === "already_cancelled"
              ? "Booking is already cancelled"
              : errCode || "Failed to cancel booking";
        return { ok: false, error: friendly };
      }

      const propertyId =
        data.property_id != null ? String(data.property_id) : null;
      if (propertyId) {
        setProperties((prev) =>
          prev.map((p) =>
            String(p.id) === propertyId
              ? {
                  ...p,
                  status: "Available" as UnitStatus,
                  hold_until: null,
                  held_by: null,
                }
              : p,
          ),
        );
      }

      try {
        await refreshCommissionLedger();
      } catch {
        // Non-blocking: cancellation already persisted
      }
      void refreshBookedContacts();

      return {
        ok: true,
        refundAmount:
          data.refund_amount != null ? Number(data.refund_amount) : undefined,
        deductionAmount:
          data.deduction_amount != null
            ? Number(data.deduction_amount)
            : undefined,
      };
    } catch (e) {
      return {
        ok: false,
        error: formatBookingMutationError(e, "Failed to cancel booking"),
      };
    }
  };

  const holdUnitForLead = async (
    propertyId: string,
    contactId: string | number,
    holdUntilIso?: string,
  ): Promise<UnitHoldResult> => {
    const result = await transitionUnitStatus(propertyId, "On_Hold", {
      contactId,
      holdUntilIso,
    });
    if (result.ok) void refreshBookedContacts();
    return result.ok
      ? { ok: true, property: result.property }
      : { ok: false, error: result.error };
  };

  const releaseUnitHold = async (
    propertyId: string,
  ): Promise<UnitHoldResult> => {
    const result = await transitionUnitStatus(propertyId, "Available");
    if (result.ok) void refreshBookedContacts();
    return result.ok
      ? { ok: true, property: result.property }
      : { ok: false, error: result.error };
  };

  // TODO(cold-auto-unlink): desk "stale_48h" / reassign_stale_leads only reassigns
  // owner — it does not clear property↔deal matches. Wire optional auto-unlink
  // here only if product later wants cold leads to release holds automatically.
  const unlinkLeadFromProperty = async (
    propertyId: string,
  ): Promise<UnitHoldResult> => {
    if (!dataProvider) {
      return { ok: false, error: "Data provider unavailable" };
    }
    if (!isUuid(propertyId) || SEED_PROPERTY_IDS.has(String(propertyId))) {
      return {
        ok: false,
        error: "Property must be a persisted UUID (seed units cannot unlink)",
      };
    }

    const existing = properties.find(
      (p) => String(p.id) === String(propertyId),
    );
    if (!existing) {
      return { ok: false, error: "Property not found" };
    }

    let hasActiveBooking = false;
    try {
      const bookingRes = await dataProvider.getList<Booking>("bookings", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "created_at", order: "DESC" },
        filter: {
          "property_id@eq": propertyId,
          "cancelled_at@is": null,
        },
      });
      hasActiveBooking = (bookingRes?.data?.length ?? 0) > 0;
    } catch (e) {
      return {
        ok: false,
        error: formatBookingMutationError(e, "Failed to check bookings"),
      };
    }

    const blocked = unlinkLeadBlockedReason(existing.status, hasActiveBooking);
    if (blocked) {
      return { ok: false, error: blocked };
    }

    // Gate hold exclusivity, then release hold BEFORE clearing deal links so a
    // failed release never leaves cancelled match + still-held unit.
    if (existing.status === "On_Hold") {
      const sale = await getCurrentSale();
      if (
        !canModifyUnitHold({
          restrictHoldChangesToHolder,
          heldBy: existing.held_by,
          currentSalesId: sale?.id != null ? String(sale.id) : null,
          isManager: Boolean(sale?.administrator),
        })
      ) {
        return { ok: false, error: HOLD_RESTRICTED_ERROR };
      }
    }

    let property = existing;
    if (existing.status === "On_Hold") {
      const released = await transitionUnitStatus(propertyId, "Available", {
        skipDealSync: true,
      });
      if (!released.ok) {
        return {
          ok: false,
          error:
            released.error ||
            "Could not release unit hold — lead was not unlinked",
        };
      }
      if (released.property) property = released.property;
    }

    let deals: Deal[] = [];
    try {
      const listed = await dataProvider.getList<Deal>("deals", {
        filter: {
          "property_id@eq": propertyId,
          "archived_at@is": null,
        },
        pagination: { page: 1, perPage: 50 },
        sort: { field: "updated_at", order: "DESC" },
      });
      deals = listed.data || [];
    } catch (e) {
      return {
        ok: false,
        error: formatBookingMutationError(e, "Failed to load linked deals"),
      };
    }

    const contactId = deals[0]?.contact_ids?.[0];

    for (const deal of deals) {
      try {
        await dataProvider.update("deals", {
          id: deal.id,
          data: { property_id: null },
          previousData: deal,
        });
      } catch (e) {
        return {
          ok: false,
          error: formatBookingMutationError(e, "Failed to clear deal link"),
        };
      }
    }

    if (isNumericContactId(contactId)) {
      try {
        const noteText = `[Unlink Lead] Unit ${existing.unit_number} unlinked`;
        await dataProvider.create("contact_notes", {
          data: {
            contact_id: Number(contactId),
            text: noteText,
            date: new Date().toISOString(),
            status: "cold",
          },
        });
      } catch {
        // Non-blocking: unlink already persisted
      }
    }

    await refreshBookedContacts();
    return { ok: true, property };
  };

  const deleteProperty = async (id: string): Promise<boolean> => {
    const isSeed = SEED_PROPERTY_IDS.has(String(id));
    try {
      if (dataProvider && !isSeed) {
        await dataProvider.delete("properties", {
          id,
          previousData: properties.find((p) => String(p.id) === String(id)),
        });
      }
      setProperties((prev) => prev.filter((p) => String(p.id) !== String(id)));
      setPropertyMedia((prev) =>
        prev.filter((m) => String(m.property_id) !== String(id)),
      );
      return true;
    } catch {
      return false;
    }
  };

  const upsertPropertyBrochure = async (
    propertyId: string,
    mediaUrl: string,
  ): Promise<PropertyMedia | null> => {
    const url = mediaUrl.trim();
    if (!url) return null;
    const isSeedProp = SEED_PROPERTY_IDS.has(String(propertyId));
    const existing = propertyMedia.find(
      (m) =>
        String(m.property_id) === String(propertyId) &&
        m.media_type === "Brochure_PDF",
    );

    try {
      if (dataProvider && !isSeedProp) {
        if (existing && !String(existing.id).startsWith("media-")) {
          const updated = await dataProvider.update("property_media", {
            id: existing.id,
            data: { media_url: url },
            previousData: existing,
          });
          if (updated?.data) {
            const row = updated.data as PropertyMedia;
            setPropertyMedia((prev) =>
              prev.map((m) => (String(m.id) === String(row.id) ? row : m)),
            );
            return row;
          }
        } else {
          const created = await dataProvider.create("property_media", {
            data: {
              property_id: propertyId,
              media_type: "Brochure_PDF",
              media_url: url,
            },
          });
          if (created?.data) {
            const row = created.data as PropertyMedia;
            setPropertyMedia((prev) => [row, ...prev]);
            return row;
          }
        }
      }
    } catch {
      // local fallback
    }

    const local: PropertyMedia = existing
      ? { ...existing, media_url: url }
      : {
          id: "media-" + Date.now(),
          property_id: propertyId,
          media_type: "Brochure_PDF",
          media_url: url,
          created_at: new Date().toISOString(),
        };
    setPropertyMedia((prev) =>
      existing
        ? prev.map((m) => (String(m.id) === String(existing.id) ? local : m))
        : [local, ...prev],
    );
    return local;
  };

  const addProject = async (
    project: Partial<Project>,
  ): Promise<Project | null> => {
    const fullProject: Partial<Project> = {
      name: project.name || "New Project",
      location_address: project.location_address || "",
      city: project.city || "Hyderabad",
      google_maps_url: project.google_maps_url || undefined,
      rera_number: project.rera_number || undefined,
      created_at: new Date().toISOString(),
    };

    try {
      if (dataProvider) {
        const created = await dataProvider.create("projects", {
          data: fullProject,
        });
        if (created?.data) {
          const row = created.data as Project;
          setProjects((prev) => withRealRow(prev, SEED_PROJECT_IDS, row));
          return row;
        }
      }
    } catch {
      // Local fallback
    }

    const fallback: Project = {
      ...(fullProject as Project),
      id: "p-" + Date.now(),
    };
    setProjects((prev) => withRealRow(prev, SEED_PROJECT_IDS, fallback));
    return fallback;
  };

  const refreshCampaignAudience = useCallback(async () => {
    if (!dataProvider) return;
    try {
      // Fetch contacts and sales independently — a sales list failure must not
      // wipe the lead picker (agents still need their RLS-visible contacts).
      const contactRes = await dataProvider.getList("contacts", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "last_seen", order: "DESC" },
        filter: {},
      });
      let salesRows: Array<{
        id: string | number;
        first_name?: string;
        last_name?: string;
      }> = [];
      try {
        const salesRes = await dataProvider.getList("sales", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        });
        salesRows = (salesRes?.data || []) as typeof salesRows;
      } catch {
        salesRows = [];
      }
      const salesName = (id?: string | number | null) => {
        if (id == null) return "Unassigned";
        const s = salesRows.find((r) => String(r.id) === String(id));
        return s
          ? `${s.first_name || ""} ${s.last_name || ""}`.trim() || "Agent"
          : "Unassigned";
      };
      const contacts = (contactRes?.data || []) as Array<
        Record<string, unknown>
      >;
      setCampaignAudience(
        contacts.map((c) => {
          const phone = phoneFromJsonb(c.phone_jsonb);
          const budget_min = c.budget_min != null ? Number(c.budget_min) : null;
          const budget_max = c.budget_max != null ? Number(c.budget_max) : null;
          return {
            id: String(c.id),
            name: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Lead",
            phone,
            budget: formatBudgetBracket(budget_min, budget_max),
            budget_min: Number.isFinite(budget_min as number)
              ? budget_min
              : null,
            budget_max: Number.isFinite(budget_max as number)
              ? budget_max
              : null,
            preferred_type: String(c.preferred_type || "—"),
            preferred_location: String(c.preferred_location || "").trim(),
            status: String(c.status || "")
              .trim()
              .toLowerCase(),
            last_contacted_days_ago: daysSince(
              (c.last_seen as string) || (c.first_seen as string),
            ),
            agent: salesName((c.sales_id as string | number) ?? null),
            sales_id: (c.sales_id as string | number) ?? null,
            has_phone: phone.replace(/\D/g, "").length >= 8,
          };
        }),
      );
    } catch (e) {
      console.warn("refreshCampaignAudience failed", e);
      // Keep any previously loaded audience instead of wiping the picker.
    }
  }, [dataProvider]);

  // Deliberately no mount-time refreshCampaignAudience() here — this context
  // wraps the entire app (Layout/MobileLayout), so an always-on effect meant
  // pulling up to 500 contacts on every page load. Callers that actually need
  // the audience (PropertyCatalog, SiteVisitList, WhatsAppBroadcast) fetch it
  // on-demand on their own mount.

  // Lightweight new-lead cue: toast + badge bump (no sound) when a contact is
  // assigned to me in real time — e.g. round-robin/portal ingest. Single
  // channel for the whole app (this provider wraps every route).
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (identity?.id == null) return;

    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(`new-lead-cue-${identity.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "contacts",
          filter: `sales_id=eq.${identity.id}`,
        },
        (payload) => {
          const row = payload.new as {
            first_name?: string;
            last_name?: string;
          };
          const name = `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim();
          notify(`New lead: ${name || "unassigned contact"}`, {
            type: "info",
          });
          queryClient.invalidateQueries({ queryKey: ["contacts"] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [identity?.id, notify, queryClient]);

  const triggerWhatsAppMessage = async (
    phone: string,
    text: string,
    openWeb: boolean = true,
    contactId?: string | number | null,
    source?: "campaign" | "agent" | "auto_first_touch" | string,
    mediaUrl?: string | null,
    extras?: { templateName?: string; templateLanguage?: string },
  ) => {
    const resolvedContactId =
      contactId != null && /^\d+$/.test(String(contactId))
        ? Number(contactId)
        : null;

    const templateName = extras?.templateName?.trim();
    const messageBody = templateName
      ? `[template:${templateName}]`
      : source === "campaign" && !text.startsWith("[Campaign] ")
        ? `[Campaign] ${text}`
        : text;
    const resolvedMediaUrl =
      typeof mediaUrl === "string" && mediaUrl.trim()
        ? mediaUrl.trim()
        : undefined;

    const logItem: {
      contact_id?: number;
      direction: "OUTBOUND";
      message_body: string;
      status: WhatsAppMessage["status"];
      created_at: string;
    } = {
      direction: "OUTBOUND",
      message_body: messageBody,
      status:
        source === "campaign"
          ? ("campaign" as WhatsAppMessage["status"])
          : "sent",
      created_at: new Date().toISOString(),
    };
    if (resolvedContactId != null) {
      logItem.contact_id = resolvedContactId;
    }

    // Meta API path: call Meta first. Only persist "sent" after a real success
    // so the CRM chat does not lie when Meta rejects the message.
    if (!openWeb) {
      const metaResult = await sendMetaCloudWhatsApp(
        phone,
        templateName ? "" : messageBody,
        resolvedMediaUrl,
        {
          contactId: resolvedContactId ?? undefined,
          skipLog: true,
          messageSource: source,
          mediaUrl: resolvedMediaUrl,
          templateName,
          templateLanguage: extras?.templateLanguage,
        },
      );
      if (!metaResult.success) {
        throw new Error(
          metaResult.error || "Meta WhatsApp API did not accept the message",
        );
      }
    } else {
      openWhatsAppWeb(phone, text || messageBody);
    }

    try {
      if (dataProvider) {
        const created = await dataProvider.create("whatsapp_messages", {
          data: logItem,
        });
        if (created?.data) {
          setWhatsappLogs((prev) => [created.data as WhatsAppMessage, ...prev]);
        }
      } else {
        const fallbackMsg: WhatsAppMessage = {
          id: "wm-" + Date.now(),
          contact_id: resolvedContactId ?? undefined,
          ...logItem,
        };
        setWhatsappLogs((prev) => [fallbackMsg, ...prev]);
      }
    } catch {
      const fallbackMsg: WhatsAppMessage = {
        id: "wm-" + Date.now(),
        contact_id: resolvedContactId ?? undefined,
        ...logItem,
      };
      setWhatsappLogs((prev) => [fallbackMsg, ...prev]);
    }

    if (resolvedContactId != null && dataProvider) {
      try {
        await dataProvider.update("contacts", {
          id: resolvedContactId,
          data: { last_seen: new Date().toISOString() },
          previousData: { id: resolvedContactId },
        });
      } catch {
        // Non-blocking: WA already sent / logged
      }
    }
  };

  const addSiteVisit = async (
    visit: Partial<SiteVisit>,
    buyerPhone: string,
    sendWhatsApp: boolean,
  ): Promise<{ ok: boolean; persisted: boolean; error?: string }> => {
    const canPersistContact =
      visit.contact_id != null && /^\d+$/.test(String(visit.contact_id));
    const canPersistAgent =
      visit.agent_id != null && /^\d+$/.test(String(visit.agent_id));
    const canPersistProperty =
      !!visit.property_id && !String(visit.property_id).startsWith("prop-");

    if (!canPersistContact) {
      return {
        ok: false,
        persisted: false,
        error: "Select a valid contact before scheduling a site visit.",
      };
    }

    const fullVisit: Partial<SiteVisit> = {
      contact_id: visit.contact_id || undefined,
      property_id: visit.property_id || properties[0]?.id,
      agent_id: visit.agent_id || undefined,
      scheduled_at: visit.scheduled_at || new Date().toISOString(),
      status: "Scheduled",
      feedback_notes: visit.feedback_notes || "Site visit scheduled.",
      created_at: new Date().toISOString(),
    };

    const persistPayload = {
      contact_id: Number(visit.contact_id),
      property_id: canPersistProperty ? visit.property_id : undefined,
      agent_id: canPersistAgent ? Number(visit.agent_id) : undefined,
      scheduled_at: fullVisit.scheduled_at,
      status: "Scheduled" as const,
      feedback_notes: fullVisit.feedback_notes,
    };

    let persisted = false;
    let persistError: string | undefined;
    try {
      if (dataProvider) {
        const created = await dataProvider.create("site_visits", {
          data: persistPayload,
        });
        if (created?.data) {
          setSiteVisits((prev) =>
            withRealRow(prev, SEED_VISIT_IDS, {
              ...(created.data as SiteVisit),
              contact_id: String(
                (created.data as SiteVisit).contact_id ?? visit.contact_id,
              ),
            }),
          );
          persisted = true;
        } else {
          persistError = "Visit create returned no data.";
        }
      } else {
        persistError = "Data provider unavailable.";
      }
    } catch (e) {
      persistError = e instanceof Error ? e.message : "Failed to save visit.";
    }

    if (!persisted) {
      const fallbackVisit: SiteVisit = {
        ...(fullVisit as SiteVisit),
        id: "sv-" + Date.now(),
      };
      setSiteVisits((prev) => withRealRow(prev, SEED_VISIT_IDS, fallbackVisit));
    }

    if (sendWhatsApp && buyerPhone) {
      const prop = properties.find(
        (p) => String(p.id) === String(visit.property_id),
      );
      const maps =
        prop?.project?.google_maps_url ||
        prop?.project?.location_address ||
        "project site";
      const msg = `Namaste! Your site visit for ${prop?.unit_number || "our project"} is confirmed for ${new Date(fullVisit.scheduled_at || "").toLocaleString("en-IN")}. Location: ${maps}`;
      try {
        await triggerWhatsAppMessage(buyerPhone, msg, true);
      } catch {
        // Visit is saved; invite open failure should not fail the schedule.
      }
    }

    return {
      ok: true,
      persisted,
      error: persisted ? undefined : persistError,
    };
  };

  const recordVisitOutcome = async (
    visitId: string,
    outcome: VisitOutcome,
    options?: RecordVisitOutcomeOptions,
  ): Promise<RecordVisitOutcomeResult> => {
    const visit = siteVisits.find((v) => String(v.id) === String(visitId));
    if (!visit) {
      return { ok: false, error: "Visit not found" };
    }
    if (outcome === "Reschedule" && !options?.scheduled_at) {
      return { ok: false, error: "scheduled_at is required for Reschedule" };
    }

    const feedbackLine =
      options?.feedback?.trim() ||
      `[Outcome: ${outcome === "No_Show" ? "No Show" : outcome}]`;
    let notesBase = visit.feedback_notes || "";
    if (outcome === "Reschedule") {
      notesBase = notesBase
        .replaceAll(REMINDER_24H_MARKER, "")
        .replaceAll(REMINDER_2H_MARKER, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }
    const nextFeedback = appendFeedbackLine(notesBase, feedbackLine);
    const patch: Partial<SiteVisit> = {
      status: outcomeToStatus(outcome),
      feedback_notes: nextFeedback,
    };
    if (outcome === "Reschedule" && options?.scheduled_at) {
      patch.scheduled_at = options.scheduled_at;
    }

    const isSeed = SEED_VISIT_IDS.has(String(visitId));
    const canPersist =
      !isSeed &&
      visit.contact_id != null &&
      /^\d+$/.test(String(visit.contact_id));

    let updatedRow: SiteVisit = { ...visit, ...patch };

    try {
      if (dataProvider && canPersist) {
        const updatePayload: Record<string, unknown> = {
          status: patch.status,
          feedback_notes: patch.feedback_notes,
        };
        if (patch.scheduled_at) {
          updatePayload.scheduled_at = patch.scheduled_at;
        }
        const updated = await dataProvider.update("site_visits", {
          id: visitId,
          data: updatePayload,
          previousData: visit,
        });
        if (updated?.data) {
          updatedRow = {
            ...(updated.data as SiteVisit),
            contact_id: String(
              (updated.data as SiteVisit).contact_id ?? visit.contact_id,
            ),
          };
        }
      }
    } catch (e) {
      return {
        ok: false,
        error: e instanceof Error ? e.message : "Failed to update visit",
      };
    }

    if (outcome === "Completed" && dataProvider && canPersist) {
      const contactId = Number(visit.contact_id);
      try {
        await dataProvider.create("tasks", {
          data: {
            contact_id: contactId,
            type: "follow-up",
            text: "Post site-visit follow-up",
            due_date: tomorrowIsoDate(),
            ...(visit.agent_id && /^\d+$/.test(String(visit.agent_id))
              ? { sales_id: Number(visit.agent_id) }
              : {}),
          },
        });
      } catch {
        // Non-blocking: visit outcome already saved
      }

      try {
        const { data: deals } = await dataProvider.getList<Deal>("deals", {
          filter: { "contact_ids@cs": `{${contactId}}` },
          pagination: { page: 1, perPage: 100 },
          sort: { field: "id", order: "ASC" },
        });
        for (const deal of deals || []) {
          if (deal.stage !== "inquiry" && deal.stage !== "site-visit") continue;
          try {
            await dataProvider.update("deals", {
              id: deal.id,
              data: { stage: "negotiation" },
              previousData: deal,
            });
          } catch {
            // Non-blocking per deal
          }
        }
      } catch {
        // Non-blocking: visit outcome already saved
      }
    }

    if (outcome === "No_Show" && dataProvider && canPersist) {
      // Mirror Completed's follow-up task, but leave deal stage untouched —
      // a no-show is not progress, just needs a reschedule attempt.
      const contactId = Number(visit.contact_id);
      try {
        await dataProvider.create("tasks", {
          data: {
            contact_id: contactId,
            type: "follow-up",
            text: "[no_show] Missed site visit — attempt to reschedule",
            due_date: tomorrowIsoDate(),
            ...(visit.agent_id && /^\d+$/.test(String(visit.agent_id))
              ? { sales_id: Number(visit.agent_id) }
              : {}),
          },
        });
      } catch {
        // Non-blocking: visit outcome already saved
      }
    }

    setSiteVisits((prev) =>
      prev.map((v) => (String(v.id) === String(visitId) ? updatedRow : v)),
    );
    return { ok: true };
  };

  const sendDueVisitReminders =
    async (): Promise<SendDueVisitRemindersResult> => {
      let sent = 0;
      const now = Date.now();

      for (const visit of siteVisits) {
        const kind = visitReminderKind(visit, now);
        if (!kind) continue;

        const lead = campaignAudience.find(
          (l) => String(l.id) === String(visit.contact_id),
        );
        const phone = lead?.phone || "";
        if (!phone) continue;

        const prop = properties.find(
          (p) => String(p.id) === String(visit.property_id),
        );
        const msg = buildVisitReminderMessage(visit, prop, kind);
        const marker =
          kind === "24h" ? REMINDER_24H_MARKER : REMINDER_2H_MARKER;

        const nextFeedback = appendFeedbackLine(visit.feedback_notes, marker);
        const isSeed = SEED_VISIT_IDS.has(String(visit.id));
        const canPersist =
          !isSeed &&
          visit.contact_id != null &&
          /^\d+$/.test(String(visit.contact_id));

        let updatedRow: SiteVisit = { ...visit, feedback_notes: nextFeedback };
        let persistedMarker = !canPersist;

        try {
          if (dataProvider && canPersist) {
            const updated = await dataProvider.update("site_visits", {
              id: visit.id,
              data: { feedback_notes: nextFeedback },
              previousData: visit,
            });
            if (updated?.data) {
              updatedRow = {
                ...(updated.data as SiteVisit),
                contact_id: String(
                  (updated.data as SiteVisit).contact_id ?? visit.contact_id,
                ),
              };
              persistedMarker = true;
            }
          } else if (!canPersist) {
            persistedMarker = true;
          }
        } catch {
          persistedMarker = false;
        }

        if (!persistedMarker) {
          continue;
        }

        await triggerWhatsAppMessage(phone, msg, true, visit.contact_id);

        setSiteVisits((prev) =>
          prev.map((v) => (String(v.id) === String(visit.id) ? updatedRow : v)),
        );
        sent += 1;
      }

      return { sent };
    };

  const addChannelPartner = async (cp: Partial<ChannelPartner>) => {
    const fullCp: Partial<ChannelPartner> = {
      agency_name: cp.agency_name || "New Agency",
      contact_person: cp.contact_person || "Agent",
      phone: cp.phone || "+91 98000 00000",
      email: cp.email,
      ...(cp.rera_number?.trim() ? { rera_number: cp.rera_number.trim() } : {}),
      commission_percentage: Number(cp.commission_percentage) || 2.0,
      created_at: new Date().toISOString(),
    };

    try {
      if (dataProvider) {
        const created = await dataProvider.create("channel_partners", {
          data: fullCp,
        });
        if (created?.data) {
          setChannelPartners((prev) =>
            withRealRow(prev, SEED_CP_IDS, created.data as ChannelPartner),
          );
          return;
        }
      }
    } catch {
      // Local fallback
    }

    const fallbackCp: ChannelPartner = {
      ...(fullCp as ChannelPartner),
      id: "cp-" + Date.now(),
    };
    setChannelPartners((prev) => withRealRow(prev, SEED_CP_IDS, fallbackCp));
  };

  const updateChannelPartner = async (
    id: string,
    patch: Partial<ChannelPartner>,
  ): Promise<ChannelPartner | null> => {
    const existing = channelPartners.find((c) => String(c.id) === String(id));
    if (!existing) return null;
    const next = { ...existing, ...patch } as ChannelPartner;
    const isSeed = SEED_CP_IDS.has(String(id));

    try {
      if (dataProvider && !isSeed) {
        const updated = await dataProvider.update("channel_partners", {
          id,
          data: patch,
          previousData: existing,
        });
        if (updated?.data) {
          const row = updated.data as ChannelPartner;
          setChannelPartners((prev) =>
            prev.map((c) => (String(c.id) === String(id) ? row : c)),
          );
          return row;
        }
      }
    } catch {
      // local fallback
    }

    setChannelPartners((prev) =>
      prev.map((c) => (String(c.id) === String(id) ? next : c)),
    );
    return next;
  };

  const deleteChannelPartner = async (id: string): Promise<boolean> => {
    const isSeed = SEED_CP_IDS.has(String(id));
    try {
      if (dataProvider && !isSeed) {
        await dataProvider.delete("channel_partners", {
          id,
          previousData: channelPartners.find(
            (c) => String(c.id) === String(id),
          ),
        });
      }
      setChannelPartners((prev) =>
        prev.filter((c) => String(c.id) !== String(id)),
      );
      return true;
    } catch {
      return false;
    }
  };

  const addVisitorFromKiosk = async (
    name: string,
    phone: string,
    visitType: "direct" | "cp",
    cpId?: string,
    projectId?: string,
  ): Promise<KioskCheckInResult> => {
    const cleanPhone = cleanIndianPhone(phone);
    if (!cleanPhone) {
      return {
        ok: false,
        assignedAgentName: "—",
        error: "Enter a valid 10-digit Indian mobile number",
      };
    }

    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] || "Walk-In";
    const lastName = nameParts.slice(1).join(" ") || "Visitor";
    const requestedCpId = visitType === "cp" && cpId ? cpId : null;
    const project = projects.find((p) => p.id === projectId) || projects[0];
    const propertyForProject =
      properties.find((p) => p.project_id === project?.id) || properties[0];
    let assignedAgentName = "Sales Desk";
    let contactId: string | number | undefined;
    let lockRetained = false;
    let cpLockedUntil: string | null = null;

    try {
      // Server-side RPC bypasses owner-scoped RLS so round-robin assignment
      // to another agent does not 403 (contacts_insert_owner_scoped).
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc("kiosk_check_in", {
        p_first_name: firstName,
        p_last_name: lastName,
        p_phone: `+91${cleanPhone}`,
        p_visit_type: visitType,
        p_channel_partner_id: requestedCpId,
        p_project_name: project?.name || null,
        p_property_id: propertyForProject?.id || null,
      });
      if (error) throw error;
      if (!data?.ok) {
        return {
          ok: false,
          assignedAgentName: "—",
          error: data?.error || "Check-in failed — try again",
        };
      }

      contactId = data.contact_id;
      assignedAgentName = data.assigned_agent_name || "Sales Desk";
      lockRetained = Boolean(data.lock_retained);
      cpLockedUntil = data.cp_locked_until ?? null;

      if (data.site_visit_id) {
        const visitRow: SiteVisit = {
          id: String(data.site_visit_id),
          contact_id: String(contactId),
          property_id: propertyForProject?.id,
          agent_id: data.sales_id != null ? String(data.sales_id) : undefined,
          scheduled_at: new Date().toISOString(),
          status: "Scheduled",
          feedback_notes: `Kiosk check-in (${visitType === "cp" ? "CP" : "direct"})${lockRetained ? " — CP lock retained" : ""}`,
          created_at: new Date().toISOString(),
        };
        setSiteVisits((prev) => withRealRow(prev, SEED_VISIT_IDS, visitRow));
      }
    } catch (err) {
      return {
        ok: false,
        assignedAgentName: "—",
        error:
          err instanceof Error ? err.message : "Check-in failed — try again",
      };
    }

    const cpDetail =
      visitType === "cp" && requestedCpId
        ? lockRetained
          ? " (existing Channel Partner lock retained)"
          : ` (Accompanied by Channel Partner)`
        : "";
    const welcomeMsg = `Namaste ${name}! Welcome to our Site Experience Center${cpDetail}. Your sales executive ${assignedAgentName} is arriving at reception.`;
    triggerWhatsAppMessage(phone, welcomeMsg, true);

    return {
      ok: true,
      contactId,
      assignedAgentName,
      lockRetained,
      cpLockedUntil,
    };
  };

  const runStalenessReassignment = async (): Promise<StalenessResult> => {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc("reassign_stale_leads", {
        p_hours: 48,
        p_limit: 20,
      });
      if (error) throw error;
      const reassigned = Number(
        (data as { reassigned?: number })?.reassigned ?? data ?? 0,
      );
      await fetchAllData();
      return {
        ok: true,
        reassigned,
        message: `Reassigned ${reassigned} lead(s) with no activity in 48+ hours via Round-Robin.`,
      };
    } catch (e) {
      return {
        ok: false,
        reassigned: 0,
        message:
          e instanceof Error
            ? e.message
            : "Failed to run 48-hour staleness reassignment",
      };
    }
  };

  return (
    <RealEstateContext.Provider
      value={{
        projects,
        properties,
        channelPartners,
        siteVisits,
        whatsappLogs,
        campaignAudience,
        usingAudienceSeed,
        leadSourceStats,
        agentLeaderboard,
        agentScorecard,
        inventoryAging,
        portalSpend,
        analyticsMetrics,
        salesAgents,
        propertyMedia,
        channelPartnerLeadStats,
        addProperty,
        updateProperty,
        transitionUnitStatus,
        createBooking,
        agentCommissionRule,
        cpCommissionLedger,
        refreshCommissionLedger,
        approveBookingDiscount,
        markCommissionPaid,
        cancelBooking,
        holdUnitForLead,
        releaseUnitHold,
        unlinkLeadFromProperty,
        deleteProperty,
        upsertPropertyBrochure,
        addProject,
        addSiteVisit,
        recordVisitOutcome,
        sendDueVisitReminders,
        addChannelPartner,
        updateChannelPartner,
        deleteChannelPartner,
        addVisitorFromKiosk,
        refreshCampaignAudience,
        triggerWhatsAppMessage,
        runStalenessReassignment,
        bookedContactByPropertyId,
        refreshBookedContacts,
      }}
    >
      {children}
    </RealEstateContext.Provider>
  );
};

export const useRealEstateContext = () => {
  const context = useContext(RealEstateContext);
  if (!context) {
    throw new Error(
      "useRealEstateContext must be used within a RealEstateProvider",
    );
  }
  return context;
};
